import type { UserTag } from '@/@types/Tagging.type';
import type { AnnualSummary } from '../model/period';
import { isVoluntaryAnnual } from './annualPresentation';

export const ANNUAL_2026_CAMPAIGN = {
	year: 2026,
	presaleEndsOn: '2026-12-04',
	flag: 'anual-2026-preventa',
} as const;

export const ANNUAL_CAMPAIGN_ID = 'anual_2026';

export const ANNUAL_OFFER_FLAG = 'anual-2026-oferta';

export type AnnualOffer = { coupon: string; price_cents: number; ends_at: string };

export const activeAnnualOffer = (value: unknown, now: Date): AnnualOffer | undefined => {
	if (!value || typeof value !== 'object') return undefined;
	const { coupon, price_cents, ends_at } = value as Partial<AnnualOffer>;
	if (typeof coupon !== 'string' || !coupon || typeof price_cents !== 'number' || price_cents <= 0 || typeof ends_at !== 'string')
		return undefined;
	const end = Date.parse(ends_at);
	return Number.isFinite(end) && end > now.getTime() ? { coupon, price_cents, ends_at } : undefined;
};

export const ANNUAL_CAMPAIGN_SECTION = 'anual_2026_card';


export type AnnualCampaignTier = 'a' | 'b' | 'c' | 'd';

export type AnnualCampaignHook = 'compliance' | 'refund' | 'catch_up' | 'comeback';

export type AnnualCampaignGate = 'relink' | 'no_valid_credential';

export type AnnualCampaignPlacement = 'cell' | 'home' | 'detail' | 'post_purchase';

export type AnnualCampaignTags = {
	tier: AnnualCampaignTier | null;
	hook: AnnualCampaignHook | null;
	gate: AnnualCampaignGate | null;
	control: boolean;
};

const TIERS: readonly AnnualCampaignTier[] = ['a', 'b', 'c', 'd'];

const HOOKS: readonly AnnualCampaignHook[] = ['compliance', 'refund', 'catch_up', 'comeback'];

const GATES: readonly AnnualCampaignGate[] = ['relink', 'no_valid_credential'];

const TAG_PREFIX = 'anual26_';

const CONTROL_TAG = `${TAG_PREFIX}control`;

const valueOf = <T extends string>(names: string[], prefix: string, allowed: readonly T[]): T | null => {
	const found = names.find((name) => name.startsWith(prefix) && allowed.includes(name.slice(prefix.length) as T));
	return found ? (found.slice(prefix.length) as T) : null;
};

export const readAnnualCampaignTags = (tags: readonly UserTag[] | undefined): AnnualCampaignTags => {
	const names = (tags ?? []).flatMap((entry) => (entry.status === 'active' && entry.tag ? [entry.tag] : []));
	return {
		tier: valueOf(names, `${TAG_PREFIX}tier_`, TIERS),
		hook: valueOf(names, `${TAG_PREFIX}hook_`, HOOKS),
		gate: valueOf(names, `${TAG_PREFIX}gate_`, GATES),
		control: names.includes(CONTROL_TAG),
	};
};

const mexicoCityDay = (now: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' }).format(now);

const presaleEndsAt = Date.parse(`${ANNUAL_2026_CAMPAIGN.presaleEndsOn}T00:00:00Z`);

const PRESALE_END_LABEL = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(presaleEndsAt));

const presaleOpen = (now: Date) => mexicoCityDay(now) <= ANNUAL_2026_CAMPAIGN.presaleEndsOn;

export const presaleLineOf = (now: Date) => (presaleOpen(now) ? `Precio de preventa hasta el ${PRESALE_END_LABEL}` : null);

export const presaleCountdownOf = (now: Date) => {
	if (!presaleOpen(now)) return null;
	const days = Math.max(0, Math.round((presaleEndsAt - Date.parse(`${mexicoCityDay(now)}T00:00:00Z`)) / 86_400_000));
	return days === 0 ? 'Último día de preventa' : days === 1 ? 'Queda 1 día de preventa' : `Quedan ${days} días de preventa`;
};

export const CAMPAIGN_TIMELINE = [
	{ month: 'Noviembre', title: 'La aseguras en preventa', detail: 'Pagas una vez y queda en nuestra fila.' },
	{ month: 'Diciembre', title: 'Deduces antes del 31', detail: 'Los gastos con factura a tu RFC se suman a tu cálculo.' },
	{ month: 'Marzo', title: 'La calculamos y te avisamos', detail: 'Te decimos el resultado antes de presentar.' },
	{ month: 'Abril', title: 'La presentamos', detail: 'El SAT la recibe del 1 al 30. Si sale a favor, pedimos tu devolución.' },
] as const;

export const campaignStepOf = (now: Date) => {
	const day = mexicoCityDay(now);
	const next = ANNUAL_2026_CAMPAIGN.year + 1;
	return presaleOpen(now) ? 0 : day < `${next}-03-01` ? 1 : day < `${next}-04-01` ? 2 : 3;
};

export const PREVIEW_STORAGE_KEY = 'anual26_preview';

export const previewAnnualCampaignTags = (): AnnualCampaignTags | undefined => {
	if (!import.meta.env.DEV) return undefined;
	const names = window.localStorage.getItem(PREVIEW_STORAGE_KEY);
	if (!names) return undefined;
	return readAnnualCampaignTags(names.split(',').map((tag) => ({ tag: tag.trim(), status: 'active' }) as UserTag));
};

export type AnnualCampaignCopy = { title: string; body: string; cta: string };

const SECURE_CTA = 'Asegurar mi anual';

const HOOK_COPY: Record<AnnualCampaignHook, AnnualCampaignCopy> = {
	compliance: {
		title: 'En abril el SAT espera tu anual 2026',
		body: 'Apártala hoy en preventa y la hacemos por ti',
		cta: SECURE_CTA,
	},
	refund: {
		title: 'Tu anual 2026 podría regresarte dinero',
		body: 'Asegúrala en preventa y en abril pedimos tu devolución al SAT',
		cta: SECURE_CTA,
	},
	catch_up: {
		title: 'Tu anual 2025 sigue pendiente',
		body: 'Ponte al corriente y deja lista la 2026 en un solo paso',
		cta: 'Ponerme al corriente',
	},
	comeback: {
		title: 'El año pasado presentamos tu anual juntos',
		body: 'La 2026 ya está en preventa, ¿repetimos?',
		cta: SECURE_CTA,
	},
};

const COMPLIANCE_WITH_PLAN: AnnualCampaignCopy = {
	title: 'Tu anual 2026 se arma con lo que ya declaraste cada mes',
	body: 'Asegúrala en preventa y la presentamos en abril de 2027',
	cta: SECURE_CTA,
};

export const RELINK_COPY: AnnualCampaignCopy = {
	title: 'Antes de asegurar tu anual, reconecta tu RFC',
	body: 'Con tu RFC reconectado podemos armar tu anual con lo que ya declaraste',
	cta: 'Reconectar mi RFC',
};

export const annualCampaignCopyOf = (hook: AnnualCampaignHook, hasPlan: boolean): AnnualCampaignCopy =>
	hook === 'compliance' && hasPlan ? COMPLIANCE_WITH_PLAN : HOOK_COPY[hook];

export const campaignCopyOf = (
	campaign: { hook: AnnualCampaignHook | null; gate: AnnualCampaignGate | null; hasPlan: boolean },
	annual?: Pick<AnnualSummary, 'reasons'>,
) =>
	campaign.gate || !campaign.hook
		? RELINK_COPY
		: annualCampaignCopyOf(campaign.hook === 'compliance' && annual && isVoluntaryAnnual(annual) ? 'refund' : campaign.hook, campaign.hasPlan);

export const SECURED_STEPS = ['Asegurada', 'La calculamos en marzo', 'Te avisamos el resultado', 'La presentamos en abril'] as const;

export const POST_PURCHASE_COPY = {
	title: 'Tu anual 2026 está asegurada',
	steps: ['La calculamos en marzo', 'Te avisamos el resultado', 'La presentamos en abril de 2027'],
	reviewCredentials: 'Revisar mis credenciales del SAT',
} as const;
